import { getErrorCode, getErrorMessage } from '@/api/errors'
import { useAzureDevOpsAllWikis } from '@/hooks'
import { connectionProblemMessage, isUnusableConnection } from '@/lib/connectionErrors'
import { MenuBookIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'

interface WikiPickerProps {
  projectId: string
  connectorId?: string
  connectionName?: string
  onReconnect?: () => void
}

export function WikiPicker({ projectId, connectorId, connectionName, onReconnect }: WikiPickerProps) {
  const { data: wikis = [], isLoading, isError, error } = useAzureDevOpsAllWikis(projectId, true, connectorId)

  if (isLoading) {
    return <div role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading wikis...</div>
  }

  if (isError) {
    if (isUnusableConnection(error)) {
      return (
        <div role="alert" className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{connectionProblemMessage(error, connectionName) ?? 'Your token has expired or been revoked.'}</span>
          {onReconnect && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReconnect}
              aria-label={`Reconnect ${connectionName ?? 'connection'} wikis`}
              data-testid={`wiki-picker-reconnect-${connectorId ?? 'connection'}`}
            >
              Reconnect
            </Button>
          )}
        </div>
      )
    }

    if (getErrorCode(error) === 'WIKI_SCOPE_MISSING') {
      return <div role="alert" className="text-sm text-destructive">{getErrorMessage(error) ?? 'The token is missing the Wiki (Read) scope.'}</div>
    }

    return <div role="alert" className="text-sm text-destructive">Failed to load wikis.</div>
  }

  if (wikis.length === 0) {
    return <div className="text-sm text-muted-foreground">No wikis found.</div>
  }

  return (
    <div className="max-h-60 overflow-y-auto rounded-md border" aria-label="Azure DevOps wikis">
      {wikis.map((wiki) => (
        <div
          key={`${wiki.project}/${wiki.id}`}
          data-testid="connector-wiki-item"
          className="flex items-center gap-2 border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
        >
          <MenuBookIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm truncate">{wiki.project}/{wiki.name}</span>
        </div>
      ))}
    </div>
  )
}
